import React from 'react';
import { PALETTE_FORCED_THEME } from '@/types/user';
const MEDIA = '(prefers-color-scheme: dark)';
const initialState = {
    theme: 'system',
    systemTheme: undefined,
    setTheme: () => null,
    palette: 'default',
    setPalette: () => null,
    animation: 'none',
    setAnimation: () => null,
};
const ThemeProviderContext = React.createContext(initialState);
export function ThemeProvider({ children, defaultTheme = 'system', defaultPalette = 'default', defaultAnimation = 'none', storageKey = 'symflow-ui-theme', paletteStorageKey = 'symflow-ui-palette', animationStorageKey = 'symflow-ui-animation', ...props }) {
    const [resolvedTheme] = React.useState(() => readStorage(storageKey));
    const [theme, setTheme] = React.useState(() => readStorage(storageKey) || defaultTheme);
    const [palette, setPalette] = React.useState(() => readStorage(paletteStorageKey) || defaultPalette);
    const [animation, setAnimation] = React.useState(() => readStorage(animationStorageKey) || defaultAnimation);
    React.useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        // Some palettes lock the rendered theme regardless of the user's
        // theme pick — Cyber + Terminal only read in dark, Y2K in light.
        // The stored `theme` is preserved (user's preference survives
        // unpicking the palette); we just override what gets applied.
        const forced = PALETTE_FORCED_THEME[palette];
        if (forced) {
            root.classList.add(forced);
            return;
        }
        if (theme === 'system') {
            const systemTheme = window.matchMedia(MEDIA).matches ? 'dark' : 'light';
            root.classList.add(systemTheme);
            return;
        }
        root.classList.add(theme);
    }, [theme, palette]);
    React.useEffect(() => {
        const root = window.document.documentElement;
        // The `default` palette is the bare shadcn baseline — leave the
        // attribute off entirely so the `[data-palette=...]` selectors
        // don't match anything.
        if (palette === 'default') {
            root.removeAttribute('data-palette');
        }
        else {
            root.setAttribute('data-palette', palette);
        }
    }, [palette]);
    React.useEffect(() => {
        const root = window.document.documentElement;
        if (animation === 'none') {
            root.removeAttribute('data-animation');
        }
        else {
            root.setAttribute('data-animation', animation);
        }
    }, [animation]);
    const value = {
        theme,
        systemTheme: resolvedTheme,
        setTheme: (theme) => {
            writeStorage(storageKey, theme);
            setTheme(theme);
        },
        palette,
        setPalette: (palette) => {
            writeStorage(paletteStorageKey, palette);
            setPalette(palette);
        },
        animation,
        setAnimation: (animation) => {
            writeStorage(animationStorageKey, animation);
            setAnimation(animation);
        },
    };
    return (<ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>);
}
export const useTheme = () => {
    const context = React.useContext(ThemeProviderContext);
    if (context === undefined)
        throw new Error('useTheme must be used within a ThemeProvider');
    return context;
};
const readStorage = (key, fallback) => {
    try {
        return localStorage.getItem(key) || fallback;
    }
    catch (e) {
        console.error(e);
        return fallback;
    }
};
const writeStorage = (key, value) => {
    try {
        localStorage.setItem(key, value);
    }
    catch (e) {
        console.error(e);
    }
};
